import React, { useState, useMemo, useCallback, memo } from 'react';
import { Search, ChevronDown, ChevronRight, Copy, Check, Moon, Sun, Eye, EyeOff, FileJson, Maximize2, Minimize2, AlertCircle, Download, Upload, RotateCcw, Settings, Zap, Code, FileText, Info } from 'lucide-react';

// Utility: Parse input into JSON
const parseInput = (input) => {
  try {
    return { data: JSON.parse(input), error: null };
  } catch (e) {
    return { data: null, error: e.message };
  }
};

// Utility: Get JSON path for a node
const getPath = (path) => path.join('.');

// Utility: Deep search in JSON
const searchInJSON = (obj, query, path = []) => {
  const results = [];
  const lowerQuery = query.toLowerCase();

  const search = (value, currentPath) => {
    if (value === null || value === undefined) return;

    const valueStr = String(value).toLowerCase();
    const key = currentPath[currentPath.length - 1] || '';
    const keyStr = String(key).toLowerCase();

    if (keyStr.includes(lowerQuery) || valueStr.includes(lowerQuery)) {
      results.push({
        path: [...currentPath],
        value,
        matchType: keyStr.includes(lowerQuery) ? 'key' : 'value'
      });
    }

    if (typeof value === 'object' && value !== null) {
      Object.entries(value).forEach(([k, v]) => {
        search(v, [...currentPath, k]);
      });
    }
  };

  search(obj, path);
  return results;
};

// Utility: Get JSON statistics
const getJSONStats = (obj) => {
  let objects = 0;
  let arrays = 0;
  let strings = 0;
  let numbers = 0;
  let booleans = 0;
  let nulls = 0;
  let maxDepth = 0;

  const traverse = (value, depth = 0) => {
    maxDepth = Math.max(maxDepth, depth);
    
    if (value === null) {
      nulls++;
    } else if (Array.isArray(value)) {
      arrays++;
      value.forEach(item => traverse(item, depth + 1));
    } else if (typeof value === 'object') {
      objects++;
      Object.values(value).forEach(v => traverse(v, depth + 1));
    } else if (typeof value === 'string') {
      strings++;
    } else if (typeof value === 'number') {
      numbers++;
    } else if (typeof value === 'boolean') {
      booleans++;
    }
  };

  traverse(obj);

  return { objects, arrays, strings, numbers, booleans, nulls, maxDepth };
};

// JSON Node Component (Memoized)
const JSONNode = memo(({ 
  data, 
  path = [], 
  searchResults, 
  currentMatch, 
  onNodeClick, 
  expandedNodes, 
  toggleNode,
  hideEmpty,
  filterMode,
  theme
}) => {
  const fullPath = getPath(path);
  const isExpanded = expandedNodes.has(fullPath);
  const isObject = typeof data === 'object' && data !== null;
  const isArray = Array.isArray(data);
  
  const hasMatch = useMemo(() => {
    return searchResults.some(r => getPath(r.path).startsWith(fullPath));
  }, [searchResults, fullPath]);

  const isCurrentMatch = useMemo(() => {
    if (currentMatch === null || !searchResults[currentMatch]) return false;
    return getPath(searchResults[currentMatch].path) === fullPath;
  }, [currentMatch, searchResults, fullPath]);

  if (hideEmpty && isObject) {
    if (isArray && data.length === 0) return null;
    if (!isArray && Object.keys(data).length === 0) return null;
  }

  if (filterMode && searchResults.length > 0 && !hasMatch) return null;

  const key = path[path.length - 1];

  const handleToggle = () => {
    if (isObject) toggleNode(fullPath);
  };

  if (!isObject) {
    const displayValue = typeof data === 'string' ? `"${data}"` : String(data);
    const valueColor = typeof data === 'string' ? 'text-emerald-600 dark:text-emerald-400' : 
                       typeof data === 'number' ? 'text-violet-600 dark:text-violet-400' :
                       typeof data === 'boolean' ? 'text-amber-600 dark:text-amber-400' :
                       'text-slate-500 dark:text-slate-400';
    
    return (
      <div 
        className={`ml-5 flex items-start gap-2 py-1.5 px-3 rounded-lg cursor-pointer transition-all duration-200 group ${
          isCurrentMatch ? 'bg-amber-100 dark:bg-amber-900/40 ring-2 ring-amber-400 shadow-sm' : 
          hasMatch ? 'bg-amber-50 dark:bg-amber-900/20' : 
          'hover:bg-slate-50 dark:hover:bg-slate-800/50'
        }`}
        onClick={() => onNodeClick(path, data)}
      >
        <span className="text-sky-600 dark:text-sky-400 font-medium shrink-0">{key}:</span>
        <span className={`${valueColor} break-all flex-1 font-mono text-sm`}>{displayValue}</span>
        <Copy 
          size={14} 
          className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" 
        />
      </div>
    );
  }

  const entries = Object.entries(data);
  const preview = isArray ? `[${data.length}]` : `{${entries.length}}`;

  return (
    <div>
      <div 
        className={`flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer transition-all duration-200 group ${
          isCurrentMatch ? 'bg-amber-100 dark:bg-amber-900/40 ring-2 ring-amber-400 shadow-sm' : 
          hasMatch ? 'bg-amber-50 dark:bg-amber-900/20' : 
          'hover:bg-slate-50 dark:hover:bg-slate-800/50'
        }`}
        onClick={handleToggle}
      >
        <div className="text-slate-400 dark:text-slate-500 transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
          <ChevronDown size={16} />
        </div>
        {key && <span className="text-sky-600 dark:text-sky-400 font-medium">{key}:</span>}
        <span className="text-slate-400 dark:text-slate-500 text-sm font-mono">{preview}</span>
      </div>
      
      {isExpanded && (
        <div className="ml-3 border-l-2 border-slate-200 dark:border-slate-700/50 pl-2 mt-1">
          {entries.map(([k, v]) => (
            <JSONNode
              key={k}
              data={v}
              path={[...path, k]}
              searchResults={searchResults}
              currentMatch={currentMatch}
              onNodeClick={onNodeClick}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              hideEmpty={hideEmpty}
              filterMode={filterMode}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// JSON Statistics Component
const JSONStatistics = memo(({ data }) => {
  const stats = useMemo(() => getJSONStats(data), [data]);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-rose-500/5 dark:from-purple-500/10 dark:via-pink-500/10 dark:to-rose-500/10 border border-purple-200/50 dark:border-purple-700/50 rounded-2xl p-5 backdrop-blur-sm">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-purple-500/10 rounded-lg">
            <Info size={18} className="text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">JSON Statistics</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Objects</div>
            <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{stats.objects}</div>
          </div>
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Arrays</div>
            <div className="text-xl font-bold text-pink-600 dark:text-pink-400">{stats.arrays}</div>
          </div>
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Max Depth</div>
            <div className="text-xl font-bold text-rose-600 dark:text-rose-400">{stats.maxDepth}</div>
          </div>
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Strings</div>
            <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.strings}</div>
          </div>
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Numbers</div>
            <div className="text-lg font-bold text-violet-600 dark:text-violet-400">{stats.numbers}</div>
          </div>
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/50 dark:border-slate-700/50">
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Booleans</div>
            <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{stats.booleans}</div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Tools Menu Component
const ToolsMenu = memo(({ onExport, onImport, onFormat, onMinify, onValidate, onClear }) => {
  const [isOpen, setIsOpen] = useState(false);

  const tools = [
    { icon: Download, label: 'Export JSON', onClick: onExport, color: 'text-blue-600' },
    { icon: Upload, label: 'Import File', onClick: onImport, color: 'text-green-600' },
    { icon: Code, label: 'Format JSON', onClick: onFormat, color: 'text-purple-600' },
    { icon: Zap, label: 'Minify JSON', onClick: onMinify, color: 'text-amber-600' },
    { icon: FileText, label: 'Validate JSON', onClick: onValidate, color: 'text-indigo-600' },
    { icon: RotateCcw, label: 'Clear All', onClick: onClear, color: 'text-red-600' },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm hover:shadow"
        title="Tools"
      >
        <Settings size={18} className={`transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-20 overflow-hidden backdrop-blur-xl">
            <div className="p-2">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 px-3 py-2">Tools & Actions</div>
              {tools.map((tool, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    tool.onClick();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left group"
                >
                  <tool.icon size={16} className={`${tool.color} dark:opacity-80 group-hover:scale-110 transition-transform`} />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{tool.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

// Main App Component
export default function JSONViewer() {
  const [input, setInput] = useState('');
  const [jsonData, setJsonData] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatch, setCurrentMatch] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['']));
  const [selectedPath, setSelectedPath] = useState(null);
  const [copied, setCopied] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [filterMode, setFilterMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showStats, setShowStats] = useState(true);

  const handleParse = useCallback(() => {
    const { data, error } = parseInput(input);
    setJsonData(data);
    setError(error);
    if (data) setExpandedNodes(new Set(['']));
  }, [input]);

  const searchResults = useMemo(() => {
    if (!jsonData || !searchQuery) return [];
    return searchInJSON(jsonData, searchQuery);
  }, [jsonData, searchQuery]);

  const toggleNode = useCallback((path) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleNodeClick = useCallback((path, value) => {
    setSelectedPath({ path: getPath(path), value });
  }, []);

  const handleCopy = useCallback((text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const navigateMatches = useCallback((direction) => {
    if (searchResults.length === 0) return;
    
    setCurrentMatch(prev => {
      if (prev === null) return 0;
      const next = direction === 'next' 
        ? (prev + 1) % searchResults.length
        : (prev - 1 + searchResults.length) % searchResults.length;
      
      const matchPath = getPath(searchResults[next].path);
      const pathParts = matchPath.split('.');
      pathParts.forEach((_, i) => {
        const subPath = pathParts.slice(0, i + 1).join('.');
        setExpandedNodes(prev => new Set([...prev, subPath]));
      });
      
      return next;
    });
  }, [searchResults]);

  const expandAll = useCallback(() => {
    const allPaths = new Set();
    const traverse = (obj, path = []) => {
      if (typeof obj === 'object' && obj !== null) {
        allPaths.add(getPath(path));
        Object.entries(obj).forEach(([k, v]) => {
          traverse(v, [...path, k]);
        });
      }
    };
    traverse(jsonData);
    setExpandedNodes(allPaths);
  }, [jsonData]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set(['']));
  }, []);

  // Tool handlers
  const handleExport = () => {
    if (!jsonData) return;
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setInput(event.target.result);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleFormat = () => {
    if (jsonData) {
      setInput(JSON.stringify(jsonData, null, 2));
    }
  };

  const handleMinify = () => {
    if (jsonData) {
      setInput(JSON.stringify(jsonData));
    }
  };

  const handleValidate = () => {
    const { data, error } = parseInput(input);
    if (error) {
      alert('Invalid JSON: ' + error);
    } else {
      alert('✓ Valid JSON');
    }
  };

  const handleClear = () => {
    setInput('');
    setJsonData(null);
    setError(null);
    setSearchQuery('');
    setSelectedPath(null);
  };

  return (
    <div className={`!text-white min-h-screen transition-all duration-300 ${darkMode ? 'dark bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-br from-slate-50 via-white to-slate-50'}`}>
      <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-slate-950 p-6' : 'max-w-[1800px] mx-auto p-6'}`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 dark:from-blue-400 dark:via-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
                JSON Viewer
              </span>
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Advanced JSON inspection and analysis tool</p>
          </div>
          <div className="flex gap-2">
            <ToolsMenu
              onExport={handleExport}
              onImport={handleImport}
              onFormat={handleFormat}
              onMinify={handleMinify}
              onValidate={handleValidate}
              onClear={handleClear}
            />
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm hover:shadow"
              title={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm hover:shadow"
            >
              {darkMode ? <Sun size={18} className="text-amber-500" /> : <Moon size={18} className="text-indigo-600" />}
            </button>
          </div>
        </div>

        <div className={`grid ${jsonData ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 max-w-3xl mx-auto'} gap-6`}>
          {/* Input Section */}
          <div className="space-y-4">
            <div className="relative overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"></div>
              <div className="p-6">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Code size={16} className="text-indigo-600 dark:text-indigo-400" />
                  Input JSON
                </label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  className="w-full h-80 p-4 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-sm bg-slate-50 dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none placeholder:text-slate-400"
                  placeholder='{"name": "example", "data": [1, 2, 3]}'
                />
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={handleParse}
                    className="flex-1 px-5 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
                  >
                    Parse JSON
                  </button>
                  {jsonData && (
                    <button
                      onClick={() => handleCopy(JSON.stringify(jsonData, null, 2), 'json')}
                      className="px-5 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm hover:shadow flex items-center gap-2 font-medium"
                    >
                      {copied === 'json' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                      <span className="text-sm">Copy</span>
                    </button>
                  )}
                </div>
                {error && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">Parse Error</div>
                        <div className="text-xs text-red-600 dark:text-red-400 font-mono">{error}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {jsonData && showStats && <JSONStatistics data={jsonData} />}
          </div>

          {/* Viewer Section */}
          {jsonData && (
            <div className="space-y-4">
              {/* Search & Controls */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-lg overflow-hidden">
                <div className="p-5 space-y-4">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-4 top-3.5 text-slate-400" size={16} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentMatch(null);
                        }}
                        placeholder="Search keys and values..."
                        className="w-full pl-11 pr-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm shadow-sm"
                      />
                    </div>
                    {searchResults.length > 0 && (
                      <div className="flex items-center gap-2 bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl px-4 border border-slate-200 dark:border-slate-700">
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          {currentMatch !== null ? currentMatch + 1 : 0} / {searchResults.length}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigateMatches('prev')}
                            className="p-2 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-colors"
                          >
                            <ChevronRight size={14} className="rotate-180" />
                          </button>
                          <button
                            onClick={() => navigateMatches('next')}
                            className="p-2 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-colors"
                          >
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={expandAll}
                      className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700"
                    >
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700"
                    >
                      Collapse All
                    </button>
                    <button
                      onClick={() => setHideEmpty(!hideEmpty)}
                      className={`px-4 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                        hideEmpty 
                          ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md' 
                          : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {hideEmpty ? <EyeOff size={12} /> : <Eye size={12} />}
                      Hide Empty
                    </button>
                    <button
                      onClick={() => setFilterMode(!filterMode)}
                      className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        filterMode 
                          ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md' 
                          : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                      }`}
                      disabled={searchResults.length === 0}
                    >
                      Filter Matches
                    </button>
                  </div>
                </div>
              </div>

              {/* Selected Path */}
              {selectedPath && (
                <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5 dark:from-emerald-500/10 dark:via-teal-500/10 dark:to-cyan-500/10 border border-emerald-200/50 dark:border-emerald-700/50 rounded-2xl p-5 backdrop-blur-sm shadow-lg">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400/10 to-teal-400/10 rounded-full blur-3xl"></div>
                  <div className="relative flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                          <Code size={14} className="text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">JSON Path</div>
                      </div>
                      <code className="block text-xs font-mono text-emerald-900 dark:text-emerald-100 break-all mb-3 bg-white/50 dark:bg-slate-900/50 p-2 rounded-lg">
                        {selectedPath.path}
                      </code>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 bg-teal-500/10 rounded-lg">
                          <FileText size={14} className="text-teal-600 dark:text-teal-400" />
                        </div>
                        <div className="text-xs font-semibold text-teal-700 dark:text-teal-300">Value</div>
                      </div>
                      <code className="block text-xs font-mono text-teal-900 dark:text-teal-100 break-all bg-white/50 dark:bg-slate-900/50 p-2 rounded-lg">
                        {JSON.stringify(selectedPath.value)}
                      </code>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopy(selectedPath.path, 'path')}
                        className="p-2.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg transition-all border border-emerald-200/50 dark:border-emerald-700/50 bg-white/50 dark:bg-slate-900/50"
                        title="Copy path"
                      >
                        {copied === 'path' ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} className="text-emerald-600 dark:text-emerald-400" />}
                      </button>
                      <button
                        onClick={() => handleCopy(JSON.stringify(selectedPath.value), 'value')}
                        className="p-2.5 hover:bg-teal-100 dark:hover:bg-teal-900/30 rounded-lg transition-all border border-teal-200/50 dark:border-teal-700/50 bg-white/50 dark:bg-slate-900/50"
                        title="Copy value"
                      >
                        {copied === 'value' ? <Check size={14} className="text-teal-600" /> : <Copy size={14} className="text-teal-600 dark:text-teal-400" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* JSON Tree */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-lg">
                <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-900/50">
                  <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <div className="p-1.5 bg-indigo-500/10 rounded-lg">
                      <FileJson size={14} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    JSON Tree Structure
                  </h2>
                </div>
                <div className="p-5 overflow-auto max-h-[700px] font-mono text-sm custom-scrollbar">
                  <JSONNode
                    data={jsonData}
                    searchResults={searchResults}
                    currentMatch={currentMatch}
                    onNodeClick={handleNodeClick}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    hideEmpty={hideEmpty}
                    filterMode={filterMode}
                    theme={darkMode ? 'dark' : 'light'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.5);
          border-radius: 4px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.3);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(203, 213, 225, 0.8);
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.5);
        }
      `}</style>
    </div>
  );
}